import { Stack } from '@mui/material';
import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { Permission } from '@procureai/permissions';
import { assocPath, pathOr } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomGridTreeDataGroupingCell } from '../../../cells/CustomGridTreeDataGroupingCell/CustomGridTreeDataGroupingCell';
import { HeaderCell } from '../../../cells/HeaderCell/HeaderCell';
import { LineItemCell } from '../../../cells/LineItemCell/LineItemCell';
import { TypeSwitchCell } from '../../../cells/TypeSwitchCell/TypeSwitchCell';
import { QUOTE_ITEM_STATE_TO_CLASS_NAME } from '../../../components/AwardingDataGrid/AwardingDataGrid.constants';
import { columnKey, createColumnFields } from '../../../components/AwardingDataGrid/AwardingDataGrid.util';
import { DemandNegotiatedQuoteItem } from '../../../DemandAwardingWidget.hooks';
import { DemandNegotiationCase, DemandNegotiationCaseQuoteItem } from '../../../DemandAwardingWidget.types';
import {
  TsrctAwardingStatusEntityVariant,
  TsrctAwardingStatusField,
  TsrctNegotiatedQuote,
  TsrctQuoteItemStatus,
  TsrctQuoteItemInterface,
  TsrctStatus,
} from '@/generated/gql/graphql';
import { CommodityLinkWithDescriptionCell } from '@/legacy/components/DataGridCustom/cells/CommodityLinkWithDescriptionCell/CommodityLinkWithDescriptionCell';
import { ItemSelectionCell } from '@/legacy/modules/DemandAwardingWidget/cells/ItemSelectionCell/ItemSelectionCell';
import { QuoteItemCell } from '@/legacy/modules/DemandAwardingWidget/cells/QuoteItemCell/QuoteItemCell';
import { RouteId } from '@/legacy/routes';
import { I18nNamespace } from '~/common/types';
import { isNegotiatedQuote } from '~/util/types/isNegotiatedQuote';

import {
  AwardingDataGridGroup,
  AwardingDataGridGroupType,
  AwardingDataGridRow,
  AwardingDataGridRowType,
  EntityStatusGetter,
  UseAwardingDataGridLineItemBiddingConfig,
} from './AwardingDataGridLineItemBidding.types';
const COL_MIN_WIDTH = 250;
const COL_MAX_WIDTH = 400;

const getQuoteLineItems = (quote: unknown): TsrctQuoteItemInterface[] => {
  if (
    typeof quote === 'object' &&
    quote !== null &&
    'lineItems' in quote &&
    Array.isArray((quote as { lineItems?: unknown }).lineItems)
  ) {
    return (quote as { lineItems: TsrctQuoteItemInterface[] }).lineItems;
  }

  return [];
};

export const useAwardingDataGridLineItemBiddingConfig: UseAwardingDataGridLineItemBiddingConfig<
  AwardingDataGridRow
> = ({ negotiationCases: dataProp, status: statusProp, demand, lineItemAssignments }) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });
  const getEntityStatus = useMemo<EntityStatusGetter>(() => {
    const statusMap = statusProp?.reduce(
      (acc, { entityId, entityVariant, field, status }) => {
        return assocPath([entityVariant, field, entityId], status, acc);
      },
      {} as Record<TsrctAwardingStatusEntityVariant, Record<TsrctAwardingStatusField, Record<string, TsrctStatus>>>,
    );
    return ({ entityVariant, field, entityId }) => {
      return pathOr(TsrctStatus.NEUTRAL, [entityVariant, field, entityId], statusMap);
    };
  }, [statusProp]);

  return useMemo(() => {
    const data = dataProp || [];
    const demandRequest = demand?.demandRequest ?? undefined;

    const demandRequestLineItems = Array.isArray(demand?.demandRequest?.lineItems)
      ? demand.demandRequest.lineItems
      : [];

    // Build a lookup table: [negotiationCaseId][demandRequestItemId] -> quote line item.
    const quoteItemsByNegotiationCaseAndRequestItemId: Record<
      string,
      Record<string, DemandNegotiationCaseQuoteItem>
    > = data.reduce(
      (acc, negotiationCase) => {
        const itemsByDemandRequestItemId: Record<string, DemandNegotiationCaseQuoteItem> = {};
        if (negotiationCase.quote && isNegotiatedQuote(negotiationCase.quote)) {
          negotiationCase.quote.lineItems.forEach((item: DemandNegotiatedQuoteItem) => {
            if (item.demandRequestItemId) {
              itemsByDemandRequestItemId[item.demandRequestItemId] = item;
            }
          });
        }
        acc[negotiationCase.id] = itemsByDemandRequestItemId;
        return acc;
      },
      {} as Record<string, Record<string, DemandNegotiationCaseQuoteItem>>,
    );

    const grps = Object.values(AwardingDataGridGroupType).reduce(
      (acc, key) => {
        acc[key as AwardingDataGridGroup] = t(`row.${key}`);
        return acc;
      },
      {} as { [key in AwardingDataGridGroup]: string },
    );
    return {
      columnHeaderHeight: 70,
      columns: data.map((entry, index) => ({
        display: 'flex',
        field: columnKey(index),
        minWidth: COL_MIN_WIDTH,
        maxWidth: COL_MAX_WIDTH,
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params.row.renderContentCell ? params.row.renderContentCell(entry) : <TypeSwitchCell {...params} />,
        renderHeader: () => (
          <HeaderCell title={entry.supplier?.name ?? ''} subTitle={t(`cell.addedBy.${entry.variant}`)} />
        ),
      })),
      rows: [
        {
          type: AwardingDataGridRowType.SUPPLIER,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.SUPPLIER}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                id: item.supplier?.internalId,
                label: item.supplier?.externalReference,
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.PROCESSED_BY,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.PROCESSED_BY}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                caseId: item.id,
                caseSequenceNumber: item.sequenceNumber,
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.PROCESS_STATUS,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.PROCESS_STATUS}`)],
          ...createColumnFields(
            (item) => ({
              status: item.status,
              processStatus: item.processStatus,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.PURCHASE_ORDERS,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.PURCHASE_ORDERS}`)],
          ...createColumnFields(
            (item) => ({
              values: item.sourceReferences,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.STATUS,
          path: [grps.grpStatus, t(`row.${AwardingDataGridRowType.STATUS}`)],
          ...createColumnFields(
            (item) => ({
              value: item.status,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.SUPPLIER_REFERENCE,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.SUPPLIER_REFERENCE}`)],
          ...createColumnFields(
            (item) => ({
              value: item.quote?.supplierReference,
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.DELIVERY_TIME_FRAME,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.DELIVERY_TIME_FRAME}`)],
          ...createColumnFields((item) => {
            return {
              value: {
                from: item.quote?.shipmentTerms?.earlyDeliveryAt,
                to: item.quote?.shipmentTerms?.lateDeliveryAt,
              },
              status: getEntityStatus({
                entityVariant: TsrctAwardingStatusEntityVariant.QUOTE,
                field: TsrctAwardingStatusField.shipmentTerms,
                entityId: item.quote?.id,
              }),
            };
          }, data),
        },
        {
          type: AwardingDataGridRowType.INITIAL_TOTAL,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.INITIAL_TOTAL}`)],
          ...createColumnFields(
            (item) =>
              item.quote && {
                value: item.quote.totalValue,
                currency: item.quote.currency,
                status: getEntityStatus({
                  entityVariant: TsrctAwardingStatusEntityVariant.QUOTE,
                  field: TsrctAwardingStatusField.quotedTotalValue,
                  entityId: item.quote?.id,
                }),
              },
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.DISCOUNT,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.DISCOUNT}`)],
          ...createColumnFields(
            (item) =>
              item.quote &&
              isNegotiatedQuote(item.quote) && {
                value: (item.quote as TsrctNegotiatedQuote).totalPriceDiscount,
                percent: (item.quote as TsrctNegotiatedQuote).discount,
                currency: item.quote.currency,
              },
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.FINAL_TOTAL,
          path: [grps.grpQuote, t(`row.${AwardingDataGridRowType.FINAL_TOTAL}`)],
          ...createColumnFields(
            (item) =>
              item.quote &&
              isNegotiatedQuote(item.quote) && {
                value: (item.quote as TsrctNegotiatedQuote).totalDiscountedValue,
                currency: item.quote.currency,
                status: getEntityStatus({
                  entityVariant: TsrctAwardingStatusEntityVariant.QUOTE,
                  field: TsrctAwardingStatusField.totalDiscountedValue,
                  entityId: item.quote?.id,
                }),
              },
            data,
          ),
        },
        // --------------------------- Awarding details ---------------------------
        ...(lineItemAssignments.hasAssignableQuoteItems
          ? [
              {
                type: AwardingDataGridRowType.AWARD_ALL_ITEMS,
                path: [grps.grpLineItems],
                renderContentCell: (negotiationCase: DemandNegotiationCase) => {
                  if (
                    !lineItemAssignments.isAwardingActiveForNegotiationCase(negotiationCase) ||
                    !lineItemAssignments.hasAssignableQuoteItemsForNegotiationCase(negotiationCase)
                  ) {
                    return null;
                  }

                  return (
                    <ItemSelectionCell
                      checked={lineItemAssignments.areAllLineItemsAssignedToNegotiationCase(negotiationCase)}
                      onToggle={() => {
                        lineItemAssignments.assignAllLineItemsToNegotiationCase(negotiationCase);
                      }}
                      label={t(`cell.itemSelection.awardAllItems`)}
                      bold
                    />
                  );
                },
              },
            ]
          : []),
        ...demandRequestLineItems.flatMap((demandRequestItem, index) => {
          return [
            {
              type: AwardingDataGridRowType.LINE_ITEM,
              path: [grps.grpLineItems, `${t(`cell.lineItem`)} ${index + 1}`],
              renderLabelCell: () => (
                <CommodityLinkWithDescriptionCell
                  description={demandRequestItem.title ?? undefined}
                  value={demandRequestItem.commodityId}
                  to={RouteId.commodityDetail(demandRequestItem.commodityId)}
                  needs={{
                    permission: Permission.BaseCommodityList,
                  }}
                />
              ),
              renderContentCell: (negotiationCase: DemandNegotiationCase) =>
                (() => {
                  const mappedLineItem =
                    quoteItemsByNegotiationCaseAndRequestItemId[negotiationCase.id]?.[demandRequestItem.id];
                  const fallbackLineItem = getQuoteLineItems(negotiationCase.quote)[index];
                  const lineItem = mappedLineItem ?? fallbackLineItem;

                  if (!lineItem) {
                    return <LineItemCell row={negotiationCase} value={null} />;
                  }

                  const lineItemAwardingStatus = getEntityStatus({
                    entityVariant: TsrctAwardingStatusEntityVariant.LINE_ITEM,
                    field: TsrctAwardingStatusField.totalDiscountedValue,
                    entityId: lineItem.id,
                  });

                  return (
                    <Stack spacing={1} width="100%">
                      {/* Negotiated quote path: line items are mapped by demandRequestItemId. */}
                      {mappedLineItem ? (
                        <QuoteItemCell
                          demandRequestItem={demandRequestItem}
                          negotiationCase={negotiationCase}
                          lineItemAssignments={lineItemAssignments}
                          quoteItem={mappedLineItem}
                        />
                      ) : fallbackLineItem ? (
                        // Fallback path: non-negotiated/legacy quote where line items are matched by row index
                        // (common before agreement is confirmed, e.g. negotiation details confirmed).
                        <QuoteItemCell
                          demandRequestItem={demandRequestItem}
                          negotiationCase={negotiationCase}
                          lineItemAssignments={lineItemAssignments}
                          quoteItem={
                            {
                              ...fallbackLineItem,
                              isAwardable:
                                (lineItemAssignments.isAwardingActiveForNegotiationCase(negotiationCase) &&
                                  fallbackLineItem.status !== TsrctQuoteItemStatus.NOT_REQUESTED &&
                                  fallbackLineItem.status !== TsrctQuoteItemStatus.OFFER_DISMISSED) ||
                                undefined,
                              demandRequestItemId: demandRequestItem.id,
                            } as DemandNegotiatedQuoteItem
                          }
                        />
                      ) : null}
                      <LineItemCell
                        row={negotiationCase}
                        value={{
                          quotedTotalSum: lineItem.totalValue ?? 0,
                          discountedTotalSum:
                            'totalDiscountedValue' in lineItem ? lineItem.totalDiscountedValue : undefined,
                          discountedTotalWithTaxes: null,
                          amount: lineItem.quantity,
                          unit: lineItem.unitId ?? demandRequestItem.unitId ?? '',
                          currency: demandRequest?.currency ?? '',
                          quotedPricePerUnit: lineItem.pricePerUnit ?? 0,
                          discountedPricePerUnit:
                            'discountedPricePerUnit' in lineItem ? lineItem.discountedPricePerUnit : undefined,
                          title: lineItem.title ?? demandRequestItem.title ?? '',
                          description: lineItem.description ?? demandRequestItem.description ?? '',
                          status: lineItemAwardingStatus,
                        }}
                      />
                    </Stack>
                  );
                })(),
              getContentCellClassName: (negotiationCase: DemandNegotiationCase) => {
                const lineItem =
                  quoteItemsByNegotiationCaseAndRequestItemId[negotiationCase.id]?.[demandRequestItem.id];
                if (lineItem) {
                  return QUOTE_ITEM_STATE_TO_CLASS_NAME[lineItem.status];
                }

                const fallbackLineItem = getQuoteLineItems(negotiationCase.quote)[index];
                return fallbackLineItem ? QUOTE_ITEM_STATE_TO_CLASS_NAME[fallbackLineItem.status] : '';
              },
            },
          ];
        }),

        {
          type: AwardingDataGridRowType.TOTAL_SPEND_PAST_YEAR,
          path: [grps.grpSupplier, t(`row.${AwardingDataGridRowType.TOTAL_SPEND_PAST_YEAR}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                supplierId: item.supplier.internalId,
                variant: 'VOLUME',
              },
            }),
            data,
          ),
        },
        {
          type: AwardingDataGridRowType.ORDERS_PAST_YEAR,
          path: [grps.grpSupplier, t(`row.${AwardingDataGridRowType.ORDERS_PAST_YEAR}`)],
          ...createColumnFields(
            (item) => ({
              value: {
                supplierId: item.supplier.internalId,
                variant: 'COUNT',
              },
            }),
            data,
          ),
        },
      ].map((item, index) => ({ id: `${index}`, ...item })),
      initialState: {
        pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD] },
      },
      groupingColDef: {
        headerName: '',
        minWidth: COL_MIN_WIDTH,
        maxWidth: COL_MAX_WIDTH,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <CustomGridTreeDataGroupingCell {...(params as any)} customContent={params.row?.renderLabelCell?.()} />
        ),
      },
      getTreeDataPath: (row) => row.path,
    };
  }, [dataProp, getEntityStatus, t, demand, lineItemAssignments]);
};
